import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import Cookies from 'js-cookie';
import { lightPalette, darkPalette, Bone } from '@apptronik/react-exoskeleton';

const ThemeContext = createContext({ darkMode: false, toggleDarkMode: () => { } });

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState<boolean>(false);

  const toggleDarkMode = () => {
    setDarkMode(prevMode => {
      const newMode = !prevMode;
      Cookies.set('darkMode', newMode, { expires: 365 });
      return newMode;
    });
  };

  const theme = createTheme({
    typography: {
      fontFamily: 'DM Sans, Arial, sans-serif', // Set DM Sans as the global font
    },
    palette: darkMode ? darkPalette : lightPalette,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            padding: '10px 20px',
            fontSize: '16px',
          },
          containedPrimary: {
            backgroundColor: darkMode ? darkPalette.primary.main : lightPalette.primary.main,
            color: darkMode ? darkPalette.text.primary : lightPalette.text.primary,
            '&:hover': {
              backgroundColor: darkMode ? darkPalette.primary.dark : lightPalette.primary.dark,
            },
          },
          containedSecondary: {
            backgroundColor: darkMode ? darkPalette.secondary.main : lightPalette.secondary.main,
            color: darkMode ? darkPalette.text.primary : lightPalette.text.primary,
            '&:hover': {
              backgroundColor: darkMode ? darkPalette.secondary.dark : lightPalette.secondary.dark,
            },
          },
          outlinedPrimary: {
            borderColor: darkMode ? darkPalette.primary.main : lightPalette.primary.main,
            color: darkMode ? darkPalette.primary.main : lightPalette.primary.main,
            '&:hover': {
              borderColor: darkMode ? darkPalette.primary.dark : lightPalette.primary.dark,
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
          outlinedSecondary: {
            borderColor: darkMode ? darkPalette.secondary.main : lightPalette.secondary.main,
            color: darkMode ? darkPalette.secondary.main : lightPalette.secondary.main,
            '&:hover': {
              borderColor: darkMode ? darkPalette.secondary.dark : lightPalette.secondary.dark,
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
          textPrimary: {
            color: darkMode ? darkPalette.primary.main : lightPalette.primary.main,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
          textSecondary: {
            color: darkMode ? darkPalette.secondary.main : lightPalette.secondary.main,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: darkMode
              ? `linear-gradient(135deg, ${Bone}, ${Bone})`
              : `linear-gradient(135deg,  ${Bone}, ${Bone})`,
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

